import { Box, Button, Grid, Typography, Divider, Alert } from "@mui/material"
import { Trans, useTranslation } from "react-i18next"
import successBanner from "../../../../assets/payment_success_banner.svg"
import { MembershipPaymentAcknowledgementPageProps } from "../../../../views/arrears-payment-flow/AcknowledgementPage/AcknowledgementPage"
import moment from "moment"
import { ArrearsRoute } from "../../../../views/arrears-payment-flow/ArrearsPaymentFlow/ArrearsPaymentFlow"
import PaymentAcknowledgementBanner from "../../PaymentAcknowledgementBanner"
import { useEffect, useState } from "react"
import { PaymentInfo, extractPaymentResponseContent, getPayment, paymentInfoFromPaymentResponse, PaymentResponse, OrderRequest, postCreateOrder, OrderStatus } from "../../../../services/ntuc/memberService/payment"
import { DeviceType } from "../../../../services/ntuc/memberService/oaMembership"
import { getMembershipPayment } from "../../../../services/ntuc/paymentService/payment"
import { LoadingButton } from "@mui/lab"
import PaymentInfoError from "../../PaymentInfoError/PaymentInfoError"

const MembershipPaymentSuccess = (props: MembershipPaymentAcknowledgementPageProps) => {
    const { t } = useTranslation(`arrears-payment`)
    const { orderId, order, onNavigateTo } = props

    const [loading, setLoading] = useState<boolean>(false)
    const [paymentResponse, setPaymentResponse] = useState<PaymentResponse>()
    const [orders, setOrder] = useState<PaymentInfo>()
    const [hasError, setHasError] = useState<boolean>(false)

    useEffect(() => {
        if (orderId) {
            getPayment(orderId)
                .then(extractPaymentResponseContent)
                .then(interceptPaymentResponse)
                .then(paymentInfoFromPaymentResponse)
                .then(setOrder)
                .catch(error => {
                    setHasError(true)
                    console.error(error.message)
                })
        }
    }, [orderId])

    const handleHomeClick = () => {
        if (window.myNtucConfig.paymentBackToHomeHomeUrl != undefined) {
            window.location.replace(window.myNtucConfig.paymentBackToHomeHomeUrl)
        } else {
            onNavigateTo(ArrearsRoute.Main)
        }
    }

    const redirectToPaymentGateway = (clientOrderId: string) => {
        window.location.replace(getMembershipPayment(clientOrderId))
    }

    const handleTryAgainClick = () => {
        const orderRequest: OrderRequest = {
            feeType: parseInt(paymentResponse!.membershipPayment.feeType),
            endDate: paymentResponse!.membershipPayment.endDate,
            startDate: paymentResponse!.membershipPayment.startDate,
            serviceType: paymentResponse!.payment.serviceType,
            noOfMonths: paymentResponse!.membershipPayment.noOfMonths,
            amount: `${paymentResponse!.payment.orderAmount}`,
            isOneTimePayment: true,
            isRecurringPayment: false,
            membershipId: parseInt(paymentResponse!.membershipPayment.membershipId),
            deviceType: DeviceType.WEB,
        }

        setLoading(true)
        postCreateOrder(orderRequest)
            .then(response => response.data.content.clientOrderId)
            .then(redirectToPaymentGateway)
            .catch(error => {
                setHasError(true)
                console.error(error.response.data.content)
            })
            .then(() => setLoading(false))
    }

    const interceptPaymentResponse = (paymentResponse: PaymentResponse): PaymentResponse => {
        setPaymentResponse(paymentResponse)
        return paymentResponse
    }

    const hasUpfrontPaymentFailed = (): boolean => orders != undefined && orders.orderStatus == OrderStatus.SEMI_SUCCESS_RECURRING

    const hasRecurringPaymentFailed = (): boolean => order != undefined && order.orderStatus == OrderStatus.SEMI_SUCCESS_ONE_TIME

    const displayedAmount = (): string => hasUpfrontPaymentFailed() ? `Failed` : ``.concat(`$`, order!.amount)

    const getDateRange = (): string => {
        if (order) {
            return `${moment(order.startDate).format(`DD MMM YYYY`)} - ${moment(order.endDate).format(`DD MMM YYYY`)}`
        }
        return ``
    }

    const getMonthTitle = (): string => {
        if (order) {
            return `${t(`payForNMonths`, { months: order.noOfMonths })}`
        }
        return ``
    }

    if (hasError) {
        return <PaymentInfoError onHomeClick={handleHomeClick} />
    }

    return(
        <Grid rowSpacing={3} container justifyContent="center" padding={2}>
            <PaymentAcknowledgementBanner bannerUrl={successBanner} alt="Payment success banner" />
            <Grid container item justifyContent="center" xs={12}>
                <Typography textAlign="center" variant="h2">{t(`arrearPaymentSuccessfulTitle`)}</Typography>
            </Grid>
            <Grid item justifyContent="center" xs={12}>
                <Typography textAlign="center">{t(`arrearPaymentSuccessfulContent`)}</Typography>
            </Grid>
            <Grid item justifyContent="center" xs={12}>
                <Typography color="text.secondary" textAlign="center">{t(`reference`)}</Typography>
                <Typography textAlign="center" data-testid="reference">{orderId}</Typography>
            </Grid>
            <Grid item justifyContent="start" xs={12}>
                <Divider />
                <Typography pt={3} mb={3} variant="h5">{t(`paymentSummary`)}</Typography>
                <Grid container item xs={12}>
                    <Grid item xs={10}>
                        <Box>
                            <Typography>{getMonthTitle()}</Typography>
                            <Typography my={2} color='text.secondary'>{getDateRange()}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={2} >
                        <Typography textAlign="end">{displayedAmount()}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item md={12} hidden={!hasUpfrontPaymentFailed()}>
                <Alert severity="error">
                    <Trans t={t}>paymentUnsuccessful</Trans>
                </Alert>
            </Grid>
            <Grid item md={12} hidden={!hasRecurringPaymentFailed()}>
                <Alert severity="error">
                    <Trans t={t}>error_recurring_payment</Trans>
                </Alert>
            </Grid>
            <Grid item justifyContent="start" xs={12}>
                <Divider />
                <Grid container item py={3} xs={12}>
                    <Grid item xs={10}>
                        <Typography mb={3} variant="h5">{t(`total`)}</Typography>
                    </Grid>
                    <Grid item xs={2} >
                        <Typography textAlign="end">{displayedAmount()}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container item xs={12} justifyContent="center" alignItems="center" columnSpacing={2} rowSpacing={3}>
                <Grid item xs={12} md={3} textAlign="center" hidden={!hasUpfrontPaymentFailed()}>
                    <LoadingButton loading={loading} disabled={loading} onClick={handleTryAgainClick} variant="contained" color="primary" size="large">
                        {t(`try_again`)}
                    </LoadingButton>
                </Grid>
                <Grid item xs={12} md={3} textAlign="center">
                    <Button onClick={handleHomeClick} variant="contained" color="primary" size="large">
                        {t(`home`)}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default MembershipPaymentSuccess